import { Formik } from 'formik';
import { GraphQLError } from 'graphql';
import pick from 'lodash/pick';
import * as querystring from 'querystring';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { useSearchParam } from 'react-use';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useKeychain } from '@treams/auth';
import { useLink } from '@treams/treams-routes';
import { Button, Callout, Input, Label, Separator, Spinner } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import WorkspaceNotFound from 'onboarding/WorkspaceNotFound';
import useWorkspaceConfiguration from 'packages/kakapo-ui-application/hooks/useWorkspaceConfiguration';
import BareLayout from 'packages/kakapo-ui-application/layouts/BareLayout';
import NewPasswordRequiredChallenge from 'packages/kakapo-ui-application/pages/auth/challenges/NewPasswordRequiredChallenge';
import PasswordChallenge from 'packages/kakapo-ui-application/pages/auth/challenges/PasswordChallenge';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';
import PublicForm from 'packages/kakapo-ui-kit/components/forms/layouts/PublicForm';
import { useWorkspace } from 'packages/kakapo-ui-services/hooks/useWorkspace';

const SeparatorLabel = styled(Box)`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	background: white;
	color: ${(props) => props.theme.colors.slateBlue};
	font-size: 14px;
	padding: 0px 5px;
`;

const renderChallenge = (
	login: string,
	challenge: graphql.Challenge,
	onCancel: () => void,
	onResponse: (response: graphql.UpdateSessionResponse) => void,
	error?: GraphQLError,
	isPrefill?: boolean,
) => {
	if (challenge.password) {
		return (
			<PasswordChallenge
				login={login}
				error={error}
				context={challenge.context}
				challenge={challenge.password}
				onCancel={onCancel}
				onResponse={onResponse}
				isPrefill={isPrefill}
			/>
		);
	}

	if (challenge.newPasswordRequired) {
		return (
			<NewPasswordRequiredChallenge
				login={login}
				context={challenge.context}
				challenge={challenge.newPasswordRequired}
				onResponse={onResponse}
				isPrefill={isPrefill}
			/>
		);
	}

	if (challenge.singleSignOn) {
		window.location.href = challenge.singleSignOn.url;
	}

	return null;
};

export default function Login() {
	const keychain = useKeychain();
	const workspace = useWorkspace();

	const home = useLink('/');

	const { t } = useTranslation();
	const history = useHistory();
	const redirect = useSearchParam('redirect');
	const location = useLocation<Record<string, string>>();
	const { data: configuration, loading } = useWorkspaceConfiguration();

	const [commit] = graphql.useLoginMutation();
	const [verify] = graphql.useVerifySessionMutation();
	const [exchange] = graphql.useExchangeSessionMutation();

	const [login, setLogin] = useState('');
	const [challenge, setChallenge] = useState<graphql.Challenge | undefined>(undefined);
	const [error, setError] = useState<GraphQLError | undefined>(undefined);

	const onCancel = () => {
		setChallenge(undefined);
		setError(undefined);
	};

	const onResponse = (response: graphql.CreateSessionResponse | graphql.UpdateSessionResponse) => {
		if (response.challenge) {
			setChallenge(response.challenge);

			return response.challenge;
		}

		if ('session' in response && response.session) {
			keychain.add(
				{
					workspaceID: workspace.id,
					userID: response.session.userID,
				},
				{
					accessToken: response.session.accessToken,
					refreshToken: response.session.refreshToken,
				},
			);

			window.location.href = redirect ?? location.state?.originalTargetUrl ?? home;
		}
	};

	useEffect(
		() =>
			void (async () => {
				if (!window.location.hash) {
					if (configuration?.redirect) {
						window.location.href = configuration.redirect;
					}

					return;
				}

				const ssoResponse = querystring.parse(window.location.hash.slice(1));

				if (workspace.id === 'support' && ssoResponse['state']) {
					const { alias } = JSON.parse(ssoResponse['state'] as string);

					window.location.href = `https://${alias}.ontreams.com/authentication${window.location.hash}`;
					return;
				}

				if (ssoResponse['error_description']) {
					if (
						ssoResponse['error_description'].includes('Invalid ProviderName/Username combination')
					) {
						history.push('/invalid-nameid');
						return;
					}
				}

				try {
					const response = await verify({
						variables: {
							input: {
								accessToken: ssoResponse['access_token'] as string,
							},
						},
					});

					keychain.add(
						{
							workspaceID: workspace.id,
							userID: response.data!.verifySession.user.id,
						},
						{
							accessToken: ssoResponse['access_token'] as string,
							refreshToken: ssoResponse['refresh_token'] as string,
						},
					);

					window.location.href = home!;
				} catch (error) {
					const { key } = error.graphQLErrors[0].extensions.error;

					if (key === 'authorization.errors.unregisteredAccount') {
						history.push('/unregistered-account');
					}
				}
			})(),
		[window.location.hash, configuration?.redirect],
	);

	useEffect(
		() =>
			void (async () => {
				const ssoResponse = querystring.parse(window.location.search.slice(1));

				if (!('code' in ssoResponse && 'state' in ssoResponse)) {
					return;
				}

				if (workspace.id === 'support' && ssoResponse['state']) {
					const { alias } = JSON.parse(ssoResponse['state'] as string);

					window.location.href = `https://${alias}.ontreams.com/authentication${window.location.search}`;
					return;
				}

				if (ssoResponse['error_description']) {
					if (
						ssoResponse['error_description'].includes('Invalid ProviderName/Username combination')
					) {
						history.push('/invalid-nameid');
						return;
					}
				}

				const state = JSON.parse(ssoResponse['state'] as string);

				try {
					const response = await exchange({
						variables: {
							input: {
								state: pick(state, ['workspaceID', 'authorizingWorkspaceID']),
								authorizationCode: ssoResponse['code'] as string,
							},
						},
					});

					keychain.add(
						{
							workspaceID: workspace.id,
							userID: response.data!.exchangeSession.session.userID,
						},
						{
							accessToken: response.data!.exchangeSession.session.accessToken,
							refreshToken: response.data!.exchangeSession.session.refreshToken,
							state,
						},
					);

					window.location.href = home!;
				} catch (error) {
					const { key } = error.graphQLErrors[0].extensions.error;

					if (key === 'authorization.errors.unregisteredAccount') {
						history.push('/unregistered-account');
					}
				}
			})(),
		[window.location.search],
	);

	const prefill = querystring.parse(window.location.search.slice(1)) as Record<string, string>;

	const onSubmit = async ({ login: value }: { login: string }) => {
		setLogin(value.trim());

		try {
			const { data } = await commit({
				variables: {
					input: {
						login: value.trim(),
					},
				},
			});

			return onResponse(data!.createSession);
		} catch (errors) {
			setError(errors.graphQLErrors[0]);
		}
	};

	const [commitPassword] = graphql.usePasswordChallengeMutation();
	useEffect(() => {
		if (!prefill.username) {
			return;
		}

		(async () => {
			const challenge = await onSubmit({ login: prefill.username });

			if (!challenge || !prefill.code) {
				return;
			}

			try {
				const { data } = await commitPassword({
					variables: {
						input: {
							response: {
								context: challenge.context,
								password: {
									username: prefill.username,
									password: prefill.code,
								},
							},
						},
					},
				});

				onResponse(data!.updateSession);
			} catch (errors) {
				setError(errors.graphQLErrors[0]);
			}
		})();
	}, [prefill.username]);

	if (!loading && !configuration) {
		return <WorkspaceNotFound />;
	}

	return (
		<BareLayout>
			{challenge ? (
				renderChallenge(login, challenge, onCancel, onResponse, error, !!prefill.username)
			) : (
				<Formik
					initialValues={{
						login: prefill['username'] ?? login,
					}}
					onSubmit={onSubmit}
				>
					{({ values }) => (
						<PublicForm>
							{loading || (!window.location.hash && configuration?.redirect) ? (
								<Flex justifyContent="center">
									<Spinner />
								</Flex>
							) : (
								<>
									<GraphQLErrorMessage error={error} />

									{configuration && configuration.singleSignOns!.length === 1 && (
										<>
											<Callout color="primary">{t('auth.login.sso.explanation')}</Callout>

											<Button title={t('auth.login.sso.button')} mt={2} mb={4} link={'/sso'} />

											<Box style={{ position: 'relative' }} mb={4}>
												<Separator />
												<SeparatorLabel>{t('auth.login.sso.alternative')}</SeparatorLabel>
											</Box>
										</>
									)}

									<Box mb={3}>
										<Label mb={2}>{t('common.userNameOrEmail')}:</Label>
										<Input.Formik name="login" autoFocus />
									</Box>

									<Button.Formik
										title={t('common.next')}
										disabled={values.login === ''}
										disableSuccess={Boolean(error)}
									/>
								</>
							)}
						</PublicForm>
					)}
				</Formik>
			)}
		</BareLayout>
	);
}
